import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import * as Tooltip from '@radix-ui/react-tooltip';

interface Guide {
  id: string;
  categoryId: string;
  name: string;
  desc: string;
  link: string;
}

interface Category {
  id: string;
  name: string;
  guides?: Guide[];
}

interface GuidesTableProps {
  guides: Guide[];
  categories: Category[];
  onEdit: (guide: Guide) => void;
  onDelete: (id: string) => void;
}

export default function GuidesTable({ guides, categories, onEdit, onDelete }: GuidesTableProps) {
  const getCategoryName = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || 'Uncategorized';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="">
      <div className="sticky top-[160px] bg-black z-10 pt-4">
        <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
              <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
                <th className="text-left py-3 px-4">Guide Name</th>
                <th className="text-left py-3 px-4">Video Link</th>
                <th className="text-left py-3 px-4 ">Description</th>
                <th className="text-left py-3 px-4">Category</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      <div className="bg-[#2A2A2A] rounded-lg overflow-hidden GuidesProseMirror">
        <table className="w-full table-fixed">
          <tbody>
            {guides.map((guide) => (
              <tr
                key={guide.id}
                className="border-b border-white/[0.08] hover:bg-[#3a3a3a33]"
              >
                <td className="py-3 px-4">
                  <span className="text-white">{guide.name}</span>
                </td>
                <td className="py-3 px-4">
                  <div className="max-w-[200px]">
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <a
                            href={guide.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-white truncate block"
                          >
                            {guide.link}
                          </a>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            className="bg-[#2A2A2A] text-white px-2 py-1 rounded text-sm max-w-[300px] break-all"
                            sideOffset={5}
                          >
                            {guide.link}
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  </div>
                </td>
                <td className="py-3 px-4 overflow-ellipsis">
                  <div className="max-w-[200px] truncate">
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <span
                            className="text-gray-400 line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: guide.desc }}
                          />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            className="bg-[#2A2A2A] text-white px-3 py-2 rounded text-sm max-w-[300px] z-[100]"
                            sideOffset={5}
                          >
                            <div
                              className="max-h-[150px] overflow-y-auto prose prose-invert"
                              dangerouslySetInnerHTML={{ __html: guide.desc }}
                            />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  </div>
                </td>
                <td className="py-3 px-4 text-gray-400">
                  {getCategoryName(guide.categoryId)}
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEdit(guide)}
                      className="p-2 hover:bg-white/[0.08] rounded-lg text-gray-400 hover:text-white"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      onClick={() => onDelete(guide.id)}
                      className="p-2 hover:bg-white/[0.08] rounded-lg text-gray-400 hover:text-red-400"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
} 